import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { SalesforceConnectionForm } from '@viq-admin/screens';
import type { SalesforceConnectionSaveBody } from '@viq-admin/screens';
import { integrationsApi } from '../../api/integrationsApi';
import { useCompanyScope } from '../../components/CompanyScopePicker';

/**
 * Create a Salesforce CRM connection for a tenant. Thin wrapper around the
 * shared `SalesforceConnectionForm mode="create"`
 * (`packages/screens/src/integrations/SalesforceConnectionForm.tsx`, p6
 * Task 2) — Refine's `useForm` is gone; this wrapper supplies the company
 * list, submits via `integrationsApi.createConnection`, and navigates back
 * to the list on success.
 *
 * `manage Integration` gates the page via the route's `RequireCan`
 * (App.tsx). The company list is the SAME cross-tenant source the console's
 * "Operate as tenant" picker uses (`useCompanyScope`, already fetched once at
 * the app root) — `companies` is threaded ONLY here, on create; the edit path
 * never passes it (the company is immutable after create, shown read-only —
 * see `edit.tsx`).
 *
 * The shared form has no built-in success/error messaging for submit (unlike
 * its delete/test flows, which own their own toasts) — this wrapper owns
 * create's.
 */
export function IntegrationCreate() {
  const { companies } = useCompanyScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(body: SalesforceConnectionSaveBody) {
    setSubmitting(true);
    try {
      await integrationsApi.createConnection(body);
      // Mark ConnectionsTable's list query stale BEFORE navigating back so
      // the list refetches on remount — the console client's 30s staleTime
      // would otherwise serve the cached rows without the new connection
      // (the invalidation Refine's useForm used to do).
      await queryClient.invalidateQueries({ queryKey: ['integrationsConnections'] });
      message.success('Connection created.');
      navigate('/integrations');
    } catch (err: unknown) {
      message.error(
        err instanceof Error
          ? err.message
          : ((err as { message?: string })?.message ?? 'Failed to create connection.'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SalesforceConnectionForm
      mode="create"
      companies={companies}
      onSubmit={handleSubmit}
      submitting={submitting}
    />
  );
}
